import Wallpaper from "../../assets/photos/15.jpg";
import { useAppContext } from "../../context/AppContext";
import { HeaderTrans, LandingTrans, OnlineClubTrans, SuccessTrans, ToasterTrans } from "../../utils/translations";
import { trackPixelEvent } from "../../utils/pixel";
import ReviewsSection from "../utilPages/reviews";
import ContactForm from "../utilPages/ContactForm";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Logo from "../../assets/logo.png";
import LoginButton from "../utilPages/LoginButton";
import LanguageToggler from "../utilPages/LanguageToggler";
import BeginnerImage from "../../assets/photos/18.jpg";
import ProImage from "../../assets/photos/13-copy.jpg";
import ProImageMobile from "../../assets/photos/19.jpg";
import { useAuth0 } from '@auth0/auth0-react';
import BtnPrimary from "../ui/BtnPrimary";
import ApproxPrice from "../ui/ApproxPrice";
import { useOnlinePrices } from "../../hooks/useOnlinePrices";
import AboutSection from "../landing/AboutSection";
import LandingScheduleSection from "../landing/LandingScheduleSection";
import OnlineClubFlipCardsGrid from "../onlineClub/OnlineClubFlipCardsGrid";
import { useScrollReveal } from "../../hooks/useScrollReveal";
import { hasActiveOnlineClubPass } from "../../utils/communityPassHelpers";
import "../../styles/landing.css";
import "../../styles/individual-landing.css";

export default function OnlineClubPage() {
  const { user, language, setToaster, isMobile, onlineClubContent } = useAppContext();
  const aboutPracticeRef = useRef(null);
  const classesRef = useRef(null);
  const scheduleRef = useRef(null);
  const aboutTeacherRef = useRef(null);
  const reviewsRef = useRef(null);
  const pricesRef = useRef(null);
  const contactsRef = useRef(null);
  const whomForRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [whomForExpanded, setWhomForExpanded] = useState(null); // null | 'beginners' | 'pros' — mobile click-to-toggle
  const { loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const onlinePrices = useOnlinePrices();

  const [aboutPracticeRevealRef, aboutPracticeVisible] = useScrollReveal();
  const [classesRevealRef, classesVisible] = useScrollReveal();
  const [whomForRevealRef, whomForVisible] = useScrollReveal();
  const [reviewsRevealRef, reviewsVisible] = useScrollReveal();
  const [pricesRevealRef, pricesVisible] = useScrollReveal();
  const [contactsRevealRef, contactsVisible] = useScrollReveal();

  const toggleWhomFor = (id) => {
    setWhomForExpanded((prev) => (prev === id ? null : id));
  };

  const scrollToSection = (ref) => {
    ref?.current?.scrollIntoView({ behavior: "smooth" });
  };

  const isIOS = () => {
    if (typeof navigator !== "undefined") {
      return /iPhone|iPad|iPod/i.test(navigator.userAgent);
    }
    return false;
  };

  const isIosDevice = isIOS();

  const handleSubscribe = () => {
    if (!user) {
      window.localStorage.setItem("redirectTo", "/subscribe");
      loginWithRedirect({ ui_locales: navigator.language || "ru" });
      loginWithRedirect({ ui_locales: navigator.language || "ru" });
      return;
    }
    if (user?.role !== 'admin' && hasActiveOnlineClubPass(user)) {
      setToaster({
        message: ToasterTrans[language].alreadySubscribed,
        type: "success",
        show: true,
      })
    } else {
      window.location.assign("/subscribe");
    }
  };

  // Eyebrow label + serif title, same as the individual classes landing
  const eyebrow = (label, className = "mb-4") => (
    <div className={`flex items-center gap-3 text-eyebrow font-sans uppercase tracking-[0.18em] text-clay ${className}`}>
      <span className="w-6 h-px bg-clay flex-shrink-0" />
      {label}
    </div>
  );
  const titleStyle = { fontSize: 'clamp(32px, 4.5vw, 52px)' };
  const reveal = (isVisible) => `transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`;

  const priceCards = [
    { key: 'single', title: SuccessTrans[language].planToggleSingle, price: onlinePrices.single, unit: OnlineClubTrans[language].singlePass, isBest: false },
    { key: 'club', title: SuccessTrans[language].planToggleSubscription, price: onlinePrices.club, unit: OnlineClubTrans[language].perMonth, isBest: true },
  ];

  const whomForCards = [
    { id: 'beginners', title: OnlineClubTrans[language].newbies, text: OnlineClubTrans[language].regardless, image: BeginnerImage },
    { id: 'pros', title: OnlineClubTrans[language].pros, text: OnlineClubTrans[language].forWhom, image: isMobile ? ProImageMobile : ProImage },
  ];

  return (
    <main className="landing-page min-h-screen scroll-smooth font-sans antialiased bg-warm-white">
      {/* Navbar — landing style */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 px-4 py-3 md:px-12 md:py-5 border-b border-clay/10"
        style={{ backgroundColor: 'rgba(250,248,244,0.9)', backdropFilter: 'blur(12px)' }}
      >
        <div className="flex items-center justify-between gap-2 max-w-[1600px] mx-auto min-w-0">
          <a href="/landing" className="flex items-center gap-1.5 md:gap-2 font-serif text-sm md:text-[17px] font-medium text-ink no-underline min-w-0 flex-shrink">
            <img alt="logo" src={Logo} className="w-7 h-7 md:w-8 md:h-8 object-contain flex-shrink-0" />
            <span className="truncate">
              <span className="text-clay">{HeaderTrans[language].onceUp}</span>
              <span className="text-ink"> {HeaderTrans[language].onMat}</span>
            </span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            <button type="button" onClick={() => { trackPixelEvent("NavigateToSeePracticeLandingPage"); scrollToSection(aboutPracticeRef); }} className="text-xs font-sans uppercase tracking-widest text-ink-mid hover:text-clay transition-colors">{HeaderTrans[language].aboutClub}</button>
            <button type="button" onClick={() => { trackPixelEvent("NavigateToPricesLandingPage"); scrollToSection(classesRef); }} className="text-xs font-sans uppercase tracking-widest text-ink-mid hover:text-clay transition-colors">{HeaderTrans[language].whatsIncluded}</button>
            <button type="button" onClick={() => { trackPixelEvent("NavigateToScheduleOnlineClub"); scrollToSection(scheduleRef); }} className="text-xs font-sans uppercase tracking-widest text-ink-mid hover:text-clay transition-colors">{HeaderTrans[language].schedule}</button>
            <button type="button" onClick={() => { trackPixelEvent("NavigateToPricesLandingPage"); scrollToSection(whomForRef); }} className="text-xs font-sans uppercase tracking-widest text-ink-mid hover:text-clay transition-colors">{HeaderTrans[language].whomFor}</button>
            <button type="button" onClick={() => { trackPixelEvent("NavigateToAboutTeacherOnlineClub"); scrollToSection(aboutTeacherRef); }} className="text-xs font-sans uppercase tracking-widest text-ink-mid hover:text-clay transition-colors">{LandingTrans[language].navAbout}</button>
            <button type="button" onClick={() => { trackPixelEvent("NavigateToReviewsLandingPage"); scrollToSection(reviewsRef); }} className="text-xs font-sans uppercase tracking-widest text-ink-mid hover:text-clay transition-colors">{HeaderTrans[language].reviews}</button>
            <button type="button" onClick={() => { trackPixelEvent("NavigateToPricesLandingPage"); scrollToSection(pricesRef); }} className="text-xs font-sans uppercase tracking-widest text-ink-mid hover:text-clay transition-colors">{HeaderTrans[language].prices}</button>
            <button type="button" onClick={() => { trackPixelEvent("NavigateToContactsLandingPage"); scrollToSection(contactsRef); }} className="text-xs font-sans uppercase tracking-widest text-ink-mid hover:text-clay transition-colors">{HeaderTrans[language].contacts}</button>
          </div>

          <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
            <span className="hidden md:inline">
              <LanguageToggler />
            </span>
            <span className="hidden md:inline">
              <LoginButton />
            </span>
            <button
              type="button"
              onClick={() => { trackPixelEvent("NavigateToBooking"); navigate("/subscribe"); setIsMenuOpen(false); }}
              className="text-xs font-sans font-medium text-clay border-b border-clay pb-0.5 hover:opacity-80 bg-transparent border-t-0 border-x-0 cursor-pointer whitespace-nowrap"
            >
              {OnlineClubTrans[language].joinCta}
            </button>
            <button type="button" onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-ink text-xl p-1 shrink-0" aria-label={LandingTrans[language].menuAria}>☰</button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 py-4 px-6 border-b border-clay/10 bg-warm-white/95 backdrop-blur-md flex flex-col gap-3">
            <button type="button" onClick={() => { trackPixelEvent("NavigateToSeePracticeLandingPage"); scrollToSection(aboutPracticeRef); setIsMenuOpen(false); }} className="text-left text-sm font-sans uppercase tracking-wider text-ink-mid hover:text-clay">{HeaderTrans[language].aboutClub}</button>
            <button type="button" onClick={() => { trackPixelEvent("NavigateToPricesLandingPage"); scrollToSection(classesRef); setIsMenuOpen(false); }} className="text-left text-sm font-sans uppercase tracking-wider text-ink-mid hover:text-clay">{HeaderTrans[language].whatsIncluded}</button>
            <button type="button" onClick={() => { trackPixelEvent("NavigateToScheduleOnlineClub"); scrollToSection(scheduleRef); setIsMenuOpen(false); }} className="text-left text-sm font-sans uppercase tracking-wider text-ink-mid hover:text-clay">{HeaderTrans[language].schedule}</button>
            <button type="button" onClick={() => { trackPixelEvent("NavigateToPricesLandingPage"); scrollToSection(whomForRef); setIsMenuOpen(false); }} className="text-left text-sm font-sans uppercase tracking-wider text-ink-mid hover:text-clay">{HeaderTrans[language].whomFor}</button>
            <button type="button" onClick={() => { trackPixelEvent("NavigateToAboutTeacherOnlineClub"); scrollToSection(aboutTeacherRef); setIsMenuOpen(false); }} className="text-left text-sm font-sans uppercase tracking-wider text-ink-mid hover:text-clay">{LandingTrans[language].navAbout}</button>
            <button type="button" onClick={() => { trackPixelEvent("NavigateToReviewsLandingPage"); scrollToSection(reviewsRef); setIsMenuOpen(false); }} className="text-left text-sm font-sans uppercase tracking-wider text-ink-mid hover:text-clay">{HeaderTrans[language].reviews}</button>
            <button type="button" onClick={() => { trackPixelEvent("NavigateToPricesLandingPage"); scrollToSection(pricesRef); setIsMenuOpen(false); }} className="text-left text-sm font-sans uppercase tracking-wider text-ink-mid hover:text-clay">{HeaderTrans[language].prices}</button>
            <button type="button" onClick={() => { trackPixelEvent("NavigateToContactsLandingPage"); scrollToSection(contactsRef); setIsMenuOpen(false); }} className="text-left text-sm font-sans uppercase tracking-wider text-ink-mid hover:text-clay">{HeaderTrans[language].contacts}</button>
            <div className="mt-auto pt-4 border-t border-clay/10 flex flex-col gap-3 text-sm">
              <LoginButton />
              <div className="md:hidden">
                <LanguageToggler />
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="relative bg-cream pt-32 pb-20 md:pt-[160px] md:pb-28 px-6 md:px-20 overflow-hidden ind-grain">
        <div className="ind-blob w-[min(520px,90vw)] h-[min(520px,90vw)] -top-20 -right-24 bg-clay" />
        <div className="ind-blob w-80 h-80 bottom-10 left-[-4rem] bg-sage-light ind-float-slow" />
        <div className="absolute top-1/3 right-[12%] w-3 h-3 rounded-full bg-clay/40 ind-float-delay hidden lg:block" aria-hidden />
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center relative z-10">
          <div>
            <div className="animate-fade-up flex items-center gap-3 text-eyebrow font-sans font-normal uppercase tracking-[0.18em] text-clay mb-7" style={{ animationDelay: '0.08s' }}>
              <span className="w-10 h-px bg-gradient-to-r from-clay to-transparent flex-shrink-0" />
              {OnlineClubTrans[language].onlineClub}
            </div>
            <h1 className="font-serif font-medium leading-[1.02] text-ink mb-8 animate-fade-up" style={{ fontSize: 'clamp(44px, 7vw, 84px)', animationDelay: '0.15s' }}>
              {OnlineClubTrans[language].once} <em className="italic text-clay font-serif">{OnlineClubTrans[language].uponamat}</em>{OnlineClubTrans[language].onceSuffix || ''}
            </h1>
            <p className="text-lg md:text-xl font-sans font-light leading-relaxed text-ink-mid max-w-[560px] mb-10 animate-fade-up border-l-2 border-clay/40 pl-6" style={{ animationDelay: '0.28s' }}>
              {OnlineClubTrans[language].description}
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: '0.4s' }}>
              <BtnPrimary onClick={() => { trackPixelEvent("NavigateToSeeMoreLandingPage"); scrollToSection(aboutPracticeRef); }}>{OnlineClubTrans[language].learnMore}</BtnPrimary>
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: '0.3s' }}>
            <div className="absolute -inset-1 bg-gradient-to-br from-clay/20 via-transparent to-sage-light/20 rounded-2xl blur-sm" />
            <img alt="girl standing on a mat" src={Wallpaper} className="relative w-full h-[50vh] lg:h-[70vh] object-cover rounded-2xl border border-sand-dark/40 shadow-xl" />
          </div>
        </div>
      </section>

      {/* About club and practice */}
      <section ref={aboutPracticeRef} className="py-24 md:py-32 px-6 md:px-20 bg-warm-white relative">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sand-dark to-transparent opacity-60" />
        <div
          ref={aboutPracticeRevealRef}
          className={`max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start ${reveal(aboutPracticeVisible)}`}
        >
          <div className="lg:sticky lg:top-28 text-left">
            {eyebrow(OnlineClubTrans[language].aboutClub, "mb-5")}
            <h2 className="font-serif font-medium leading-tight text-ink mb-6" style={titleStyle}>
              {OnlineClubTrans[language].aboutClubTitle}
              <em className="italic text-clay">{OnlineClubTrans[language].aboutClubTitleEm}</em>
            </h2>
            <p className="text-[17px] font-sans font-light leading-relaxed text-ink-mid">{OnlineClubTrans[language].aboutClubDescription}</p>
            <div className="mt-10 relative">
              <div className="absolute -inset-1 bg-gradient-to-br from-clay/20 via-transparent to-sage-light/20 rounded-2xl blur-sm" />
              <blockquote className="relative font-serif text-lg md:text-xl italic text-ink leading-relaxed p-8 md:p-9 bg-white border border-sand-dark/40 rounded-2xl shadow-sm -rotate-[0.35deg]">
                <strong className="not-italic font-medium text-clay">{OnlineClubTrans[language].value}:</strong> {OnlineClubTrans[language].valueDescription}
              </blockquote>
            </div>
          </div>
          <article className="group relative text-left bg-white border border-sand-dark/60 rounded-2xl p-8 md:p-10 shadow-[0_1px_0_rgba(42,36,32,0.04)] hover:shadow-xl hover:border-clay/25 hover:-translate-y-1 transition-all duration-300">
            <h3 className="font-serif text-2xl md:text-[26px] font-medium text-ink mb-4 leading-snug">{OnlineClubTrans[language].whatIsThisPractice}</h3>
            <p className="text-sm md:text-[15px] font-sans font-light leading-relaxed text-ink-mid">{OnlineClubTrans[language].practiceDescription}</p>
            <div className="mt-8 pt-6 border-t border-sand-dark/70">
              <p className="text-sm font-sans font-medium text-ink mb-2">{OnlineClubTrans[language].duration}:</p>
              <p className="text-sm font-sans font-light text-ink-mid mb-4">{OnlineClubTrans[language].notScary}</p>
              <ul className="flex flex-col">
                {[OnlineClubTrans[language].warmup, OnlineClubTrans[language].mainBlock, OnlineClubTrans[language].shavasana].map((step, i) => (
                  <li key={i} className="flex gap-4 items-center py-3 border-b border-sand-dark/50 last:border-b-0">
                    <span className="font-serif text-sm text-clay flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-xl bg-cream group-hover:bg-clay/10 transition-colors">{String(i + 1).padStart(2, '0')}</span>
                    <span className="text-sm md:text-[15px] font-sans font-light text-ink-mid">{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        </div>
      </section>

      {/* What's included */}
      <section ref={classesRef} className="py-24 md:py-32 px-6 md:px-20 bg-cream relative overflow-hidden ind-grain">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sand-dark to-transparent opacity-60" />
        <div ref={classesRevealRef} className={`max-w-6xl mx-auto ${reveal(classesVisible)}`}>
          {eyebrow(HeaderTrans[language].whatsIncluded)}
          <h2 className="font-serif font-medium leading-tight text-ink mb-16 max-w-3xl" style={titleStyle}>
            {OnlineClubTrans[language].formatsTitle}
            <em className="italic text-clay">{OnlineClubTrans[language].formatsTitleEm}</em>
          </h2>
          <OnlineClubFlipCardsGrid
            items={onlineClubContent}
            language={language}
            isMobile={isMobile}
            setToaster={setToaster}
          />
        </div>
      </section>

      <div ref={scheduleRef} className="border-t border-sand-dark/30">
        <LandingScheduleSection
          showLocationLinks={false}
          suppressSlotTitles={['Йога в студии', 'Йога в парке']}
          customScheduleHeading={{
            omitLabel: true,
            title: OnlineClubTrans[language].scheduleSectionHeadingTitle,
          }}
        />
      </div>

      {/* Whom for */}
      <section ref={whomForRef} className={`py-24 md:py-32 px-6 md:px-20 bg-warm-white relative ${isIosDevice ? 'bg-scroll' : 'bg-fixed'}`}>
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sand-dark to-transparent opacity-60" />
        <div ref={whomForRevealRef} className={`max-w-5xl mx-auto ${reveal(whomForVisible)}`}>
          {eyebrow(HeaderTrans[language].whomFor)}
          <h2 className="font-serif font-medium leading-tight text-ink mb-16 max-w-3xl" style={titleStyle}>
            {OnlineClubTrans[language].whoItFitsTitle}
            <em className="italic text-clay">{OnlineClubTrans[language].whoItFitsTitleEm}</em>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {whomForCards.map((card) => {
              const expanded = whomForExpanded === card.id;
              return (
                <article key={card.id} className="group">
                  <h3 className="font-serif text-xl md:text-[22px] font-medium text-ink mb-4 text-center leading-snug">{card.title}</h3>
                  <div
                    role="button"
                    tabIndex={isMobile ? 0 : -1}
                    aria-expanded={isMobile ? expanded : undefined}
                    onClick={() => isMobile && toggleWhomFor(card.id)}
                    onKeyDown={(e) => { if (isMobile && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggleWhomFor(card.id); } }}
                    className="relative rounded-2xl overflow-hidden bg-cover bg-center h-80 md:h-96 cursor-pointer md:cursor-default border border-sand-dark/60 shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                    style={{ backgroundImage: `url(${card.image})` }}
                  >
                    <div className={`absolute inset-0 transition duration-500 ease-in-out ${isMobile ? (expanded ? 'bg-ink/60' : '') : 'group-hover:bg-ink/60'}`} />
                    <div className={`absolute inset-0 flex flex-col items-center justify-center text-white transition duration-700 ease-out text-center px-6 md:px-10 ${isMobile ? (expanded ? 'opacity-100' : 'opacity-0') : 'opacity-0 group-hover:opacity-100'}`}>
                      <p className="text-sm md:text-base font-sans font-normal text-white">{card.text}</p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <div ref={aboutTeacherRef} className="border-t border-sand-dark/30">
        <AboutSection />
      </div>

      {/* Reviews */}
      <section ref={reviewsRef} className="w-full bg-cream border-t border-sand-dark/30 ind-grain">
        <div ref={reviewsRevealRef} className={reveal(reviewsVisible)}>
          <ReviewsSection />
        </div>
      </section>

      {/* Prices — same card layout as the individual classes landing */}
      <section ref={pricesRef} className="py-24 md:py-32 px-6 md:px-20 bg-warm-white relative">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sand-dark to-transparent opacity-60" />
        <div ref={pricesRevealRef} className={`max-w-4xl mx-auto ${reveal(pricesVisible)}`}>
          {eyebrow(HeaderTrans[language].prices)}
          <h2 className="font-serif font-medium leading-tight text-ink mb-12 md:mb-16" style={titleStyle}>
            {OnlineClubTrans[language].onlineClub}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {priceCards.map((card) => (
              <div
                key={card.key}
                className={`flex flex-col rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl ${
                  card.isBest
                    ? 'bg-gradient-to-b from-clay to-clay-dark text-white ring-2 ring-clay/30 ring-offset-4 ring-offset-warm-white shadow-xl'
                    : 'bg-white border border-sand-dark/50 shadow-lg hover:border-clay/20'
                }`}
              >
                <div className="p-7 md:p-8 flex flex-col flex-1">
                  <div className={`font-serif text-lg font-medium leading-tight mb-6 ${card.isBest ? 'text-white' : 'text-ink'}`}>{card.title}</div>
                  <div className={`font-serif text-4xl md:text-[2.75rem] font-normal leading-none ${card.isBest ? 'text-white' : 'text-clay'}`}>
                    <ApproxPrice
                      eur={card.price.eur}
                      charges={[{ amount: card.price.ils, currency: 'ILS' }, { amount: card.price.rub, currency: 'RUB' }]}
                      subClassName={`mt-3 leading-normal ${card.isBest ? 'text-white/70' : 'text-ink-light'}`}
                    />
                  </div>
                  <div className={`text-xs font-sans font-light mt-2 mb-6 ${card.isBest ? 'text-white/65' : 'text-ink-light'}`}>{card.unit}</div>
                  {card.isBest && language === "russian" && (
                    <p className="text-xs font-sans font-light text-white/80 mb-6">
                      Вы будете перенаправлены в клуб сразу после оформления подписки.
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => language === "russian" && handleSubscribe()}
                    className={`mt-auto w-full py-3.5 rounded-lg font-sans text-[12px] font-semibold tracking-wider uppercase transition-all ${
                      card.isBest ? 'bg-white text-clay-dark hover:bg-cream shadow-md' : 'bg-ink text-white hover:bg-[#3a2e2a]'
                    }`}
                  >
                    {language === "russian" ? "К оплате" : "Coming soon"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contacts — contact form instead of map */}
      <section ref={contactsRef} className="py-24 md:py-32 px-6 md:px-20 bg-cream relative overflow-hidden ind-grain">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sand-dark to-transparent opacity-60" />
        <div
          ref={contactsRevealRef}
          className={`grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 max-w-6xl mx-auto ${reveal(contactsVisible)}`}
        >
          <div className="min-w-0 text-left">
            {eyebrow(LandingTrans[language].contactLabel)}
            <h2 className="font-serif font-medium leading-tight text-ink mb-10" style={titleStyle}>
              {LandingTrans[language].contactTitle}<em className="italic text-clay">{LandingTrans[language].contactTitleEm}</em>
            </h2>
            <div className="space-y-5">
              <div className="bg-white border border-sand-dark/60 rounded-2xl p-6 shadow-sm">
                <span className="block text-left text-[11px] font-sans font-medium uppercase tracking-wider text-clay mb-1">
                  {LandingTrans[language].whatsappLabel}
                </span>
                <a
                  href="https://wa.me/972559116975"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[15px] font-sans font-light text-ink-mid border-b border-sand-dark hover:border-clay hover:text-clay"
                >
                  +972 55 911 69 75
                </a>
              </div>
              <div className="bg-white border border-sand-dark/60 rounded-2xl p-6 shadow-sm">
                <span className="block text-left text-[11px] font-sans font-medium uppercase tracking-wider text-clay mb-1">
                  Telegram
                </span>
                <a
                  href="https://t.me/joykayoga"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[15px] font-sans font-light text-ink-mid border-b border-sand-dark hover:border-clay hover:text-clay"
                >
                  @joykayoga
                </a>
              </div>
            </div>
          </div>
          <div className="min-w-0 relative">
            <div className="absolute -inset-1 bg-gradient-to-br from-clay/15 via-transparent to-sage-light/15 rounded-2xl blur-sm" />
            <div className="relative bg-white/90 backdrop-blur-sm border border-sand-dark/50 rounded-2xl p-6 md:p-8 shadow-sm">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
